import React, { useState } from "react";
import api from "../../../../features/auth/axiosConfig";
import { ProfileData } from "../types/profile";
import UserProfileForm from "./UserProfileForm";
import { useUserProfileForm } from "../hooks/useUserProfileForm";
import CommonButtons from "../../../components/CommonButtons";
import { ModalWrapper } from "../../../../shared/components/ModalWrapper";
import { notifyError, notifySuccess } from "src/assets/toastService";

interface EditUserProfileProps {
  profileData: ProfileData | null;
  onClose: () => void;
  onSave: () => void;
}

const EditUserProfile: React.FC<EditUserProfileProps> = ({ profileData, onClose, onSave }) => {
  const { formData, handleChange } = useUserProfileForm(profileData);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!profileData) return;

    const payload: any = {};

    if (formData.firstName !== (profileData.firstName || "")) {
      payload.firstName = formData.firstName;
    }

    if (formData.middleName !== (profileData.middleName || "")) {
      payload.middleName = formData.middleName;
    }

    if (formData.lastName !== (profileData.lastName || "")) {
      payload.lastName = formData.lastName;
    }

    const originalDob = profileData.dob ? (typeof profileData.dob === "string" ? profileData.dob.split("T")[0] : profileData.dob.$date.split("T")[0]) : "";

    if (formData.dob !== originalDob) {
      payload.dob = formData.dob;
    }

    if (formData.addressLine1 !== (profileData.addressLine1 || "")) {
      payload.addressLine1 = formData.addressLine1;
    }

    // Compare emails
    if (
      JSON.stringify(formData.emails) !==
      JSON.stringify(
        profileData.emails?.map((email) => ({
          email: email.email || "",
          isPrimary: email.isPrimary ?? true,
        })) || [],
      )
    ) {
      payload.emails = formData.emails;
    }

    // Compare phones
    if (
      JSON.stringify(formData.phones) !==
      JSON.stringify(
        profileData.phones?.map((phone) => ({
          phone: phone.phone || "",
          isPrimary: phone.isPrimary ?? true,
        })) || [],
      )
    ) {
      payload.phones = formData.phones;
    }

    // Nothing changed
    if (Object.keys(payload).length === 0) {
      notifyError("No changes detected");
      return;
    }

    setLoading(true);

    try {
      await api.put(`/pos/v2/profile/${profileData.orgId}`, payload);

      notifySuccess("Profile updated successfully!");

      setTimeout(() => {
        onSave();
        onClose();
      }, 1000);
    } catch (error: any) {
      console.error(error);
      notifyError(error?.response?.data?.message || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  if (!profileData) return null;

  return (
    <ModalWrapper title="Edit User Profile" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <UserProfileForm formData={formData} handleChange={handleChange} />
        <div className="flex justify-end gap-6 pt-4">
          <CommonButtons className="w-36" label="CANCEL" variant="outlineDanger" onClick={onClose} />
          <CommonButtons className="w-36" label={loading ? "Saving..." : "Confirm"} type="submit" disabled={loading} />
        </div>
      </form>
    </ModalWrapper>
  );
};

export default EditUserProfile;
